import React, {useRef, useState} from 'react';
import {Designer} from '@pdfme/ui';
import {Template} from '@pdfme/common';
import {text} from '@pdfme/schemas';
import {PDFDocument, rgb, degrees} from 'pdf-lib';
import {Buffer} from 'buffer';
import LoadingScreen from "../components/LoadingScreen";

// Ensure Buffer is available
if (typeof window !== 'undefined') {
    window.Buffer = window.Buffer || Buffer;
}

const MM_TO_PT = 2.83465;

interface ItemCodeMapping {
    id: string;
    itemCode: string;
    startPage: number;
    endPage: number;
}

interface DrawingInfo {
    revision: string;
    date: string;
    projectName: string;
    drawingTitle: string;
    logo: File | null;
}

export const PdfCropper = () => {
    const designerRef = useRef<HTMLDivElement>(null);
    const designerInstance = useRef<Designer | null>(null);

    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [startPage, setStartPage] = useState<number>(1);
    const [isReadyToDraw, setIsReadyToDraw] = useState(false);
    const [processing, setProcessing] = useState(false);

    // Form fields
    const [drawingInfo, setDrawingInfo] = useState<DrawingInfo>({
        revision: '',
        date: new Date().toISOString().split('T')[0].split('-').reverse().join('/'),
        projectName: '',
        drawingTitle: '',
        logo: null
    });

    // Item code mappings
    const [itemMappings, setItemMappings] = useState<ItemCodeMapping[]>([
        {id: crypto.randomUUID(), itemCode: '', startPage: 1, endPage: 1}
    ]);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setPdfFile(e.target.files[0]);
            setIsReadyToDraw(false);
        }
    };

    const onLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setDrawingInfo({...drawingInfo, logo: e.target.files[0]});
        }
    };

    const addItemMapping = () => {
        setItemMappings([
            ...itemMappings,
            {
                id: crypto.randomUUID(),
                itemCode: '',
                startPage: itemMappings[itemMappings.length - 1]?.endPage + 1 || 1,
                endPage: itemMappings[itemMappings.length - 1]?.endPage + 1 || 1
            }
        ]);
    };

    const removeItemMapping = (id: string) => {
        if (itemMappings.length > 1) {
            setItemMappings(itemMappings.filter(m => m.id !== id));
        }
    };

    const updateItemMapping = (id: string, field: keyof ItemCodeMapping, value: string | number) => {
        setItemMappings(itemMappings.map(m =>
            m.id === id ? {...m, [field]: value} : m
        ));
    };

    const loadDesigner = async () => {
        if (!pdfFile || !designerRef.current) return;

        try {
            const fileArrayBuffer = await pdfFile.arrayBuffer();
            const pdfDoc = await PDFDocument.load(fileArrayBuffer);

            if (startPage < 1 || startPage > pdfDoc.getPageCount()) {
                alert(`Please enter a page number between 1 and ${pdfDoc.getPageCount()}`);
                return;
            }

            const singlePagePdf = await PDFDocument.create();
            const [copiedPage] = await singlePagePdf.copyPages(pdfDoc, [startPage - 1]);
            singlePagePdf.addPage(copiedPage);

            const basePdfBytes = await singlePagePdf.save();
            const basePdfBase64 = Buffer.from(basePdfBytes).toString('base64');
            const basePdfDataUrl = `data:application/pdf;base64,${basePdfBase64}`;

            const template: Template = {
                basePdf: basePdfDataUrl,
                schemas: [
                    [
                        {
                            name: "crop_marker",
                            type: "text",
                            position: {x: 20, y: 20},
                            width: 100,
                            height: 100,
                            content: "RESIZE ME TO CROP",
                            ...({
                                backgroundColor: "#00ff0022",
                                borderColor: "#00ff00",
                                borderWidth: 1,
                                fontSize: 36,
                                fontName: "Roboto"
                            } as any)
                        }
                    ]
                ]
            };

            if (designerInstance.current) {
                designerInstance.current.destroy();
            }

            designerInstance.current = new Designer({
                domContainer: designerRef.current,
                template,
                plugins: {text},
                options: {sidebarOpen: false}
            });

            setIsReadyToDraw(true);

        } catch (err) {
            console.error("Error loading PDF", err);
            alert("Failed to load PDF.");
        }
    };

    const getItemCodeForPage = (pageNum: number): string => {
        const mapping = itemMappings.find(m =>
            pageNum >= m.startPage && pageNum <= m.endPage
        );
        return mapping?.itemCode || '';
    };

    const handleCropAndDownload = async () => {
        if (!pdfFile || !designerInstance.current) return;

        // Validation
        if (!drawingInfo.projectName.trim()) {
            alert('Please enter a project name');
            return;
        }
        if (!drawingInfo.drawingTitle.trim()) {
            alert('Please enter a drawing title');
            return;
        }

        setProcessing(true);

        try {
            const template = designerInstance.current.getTemplate();
            const pageOneSchemas = template.schemas[0];
            const schema = pageOneSchemas.find((s: { name: string; }) => s.name === "crop_marker");

            if (!schema) {
                throw new Error("Crop marker not found");
            }

            const boxX_mm = schema.position.x;
            const boxY_mm = schema.position.y;
            const boxW_mm = schema.width;
            const boxH_mm = schema.height;

            // Load template PDF
            const templateResponse = await fetch(`${process.env.REACT_APP_PUBLIC_URL}/assets/drawing_template.pdf`);
            const templateBytes = await templateResponse.arrayBuffer();

            // Load original PDF
            const fileArrayBuffer = await pdfFile.arrayBuffer();
            const originalPdf = await PDFDocument.load(fileArrayBuffer);
            const totalPages = originalPdf.getPageCount();

            // Get the FIRST page dimensions to calculate percentages
            const firstPage = originalPdf.getPage(startPage - 1);
            const firstPageSize = firstPage.getSize();

            // Convert crop box from mm to points for the first page
            const boxX_pt = boxX_mm * MM_TO_PT;
            const boxY_pt = boxY_mm * MM_TO_PT;
            const boxW_pt = boxW_mm * MM_TO_PT;
            const boxH_pt = boxH_mm * MM_TO_PT;

            // Calculate crop box as PERCENTAGES of the first page
            const cropXPercent = boxX_pt / firstPageSize.width;
            const cropYPercent = boxY_pt / firstPageSize.height;
            const cropWidthPercent = boxW_pt / firstPageSize.width;
            const cropHeightPercent = boxH_pt / firstPageSize.height;

            console.log('Crop percentages:', {
                x: cropXPercent,
                y: cropYPercent,
                width: cropWidthPercent,
                height: cropHeightPercent
            });

            // Load logo if provided
            let logoImageBytes = null;
            if (drawingInfo.logo) {
                logoImageBytes = await drawingInfo.logo.arrayBuffer();
            }

            // Create final PDF
            const finalPdf = await PDFDocument.create();

            // Process each page from startPage onwards
            for (let pageIndex = startPage - 1; pageIndex < totalPages; pageIndex++) {
                // Load fresh template for this page
                const templatePdf = await PDFDocument.load(templateBytes);
                const [templatePage] = await finalPdf.copyPages(templatePdf, [0]);

                const templateSize = templatePage.getSize();

                // Copy the content page
                const [contentPage] = await finalPdf.copyPages(originalPdf, [pageIndex]);
                const currentPageSize = contentPage.getSize();

                // Apply crop box percentages to THIS page's dimensions
                const x_pt = cropXPercent * currentPageSize.width;
                const y_pt_from_top = cropYPercent * currentPageSize.height;
                const w_pt = cropWidthPercent * currentPageSize.width;
                const h_pt = cropHeightPercent * currentPageSize.height;

                // Convert y from top to bottom (PDF coordinates)
                const y_pt = currentPageSize.height - y_pt_from_top - h_pt;

                // Set crop box on content page
                contentPage.setCropBox(x_pt, y_pt, w_pt, h_pt);

                // Calculate scaling to make cropped content at least 70% of template
                // while respecting max width of 900pt
                const targetMinWidth = templateSize.width * 0.7;
                const targetMinHeight = templateSize.height * 0.7;
                const maxWidth = 1000;

                // Calculate scale needed for each constraint
                const scaleForMinWidth = targetMinWidth / w_pt;
                const scaleForMinHeight = targetMinHeight / h_pt;
                const scaleForMaxWidth = maxWidth / w_pt;

                // Use the minimum of:
                // 1. Scale needed to meet 70% requirement (width or height)
                // 2. Scale needed to stay under 900pt width cap
                const scale = Math.min(
                    Math.max(scaleForMinWidth, scaleForMinHeight),
                    scaleForMaxWidth
                );

                // Calculate scaled dimensions (maintaining aspect ratio)
                const scaledWidth = w_pt * scale;
                const scaledHeight = h_pt * scale;

                // Center the content on the template
                const centerX = (templateSize.width - scaledWidth) / 2;
                const centerY = (templateSize.height - scaledHeight) / 2 + 50;

                // Embed the cropped page as XObject
                const contentXObject = await finalPdf.embedPage(contentPage, {
                    left: x_pt,
                    right: x_pt + w_pt,
                    bottom: y_pt,
                    top: y_pt + h_pt
                });

                // Draw the cropped content centered and scaled
                templatePage.drawPage(contentXObject, {
                    x: centerX,
                    y: centerY,
                    width: scaledWidth,
                    height: scaledHeight
                });

                // Add text fields to template
                const fontSize = 10;
                const textColor = rgb(0, 0, 0);

                // Get item code for this page (1-indexed)
                const currentPageNum = pageIndex - startPage + 2;
                const itemCode = getItemCodeForPage(currentPageNum);

                // ITEM CODE - top right
                templatePage.drawText(itemCode, {
                    x: templateSize.width - 215,
                    y: 105,
                    size: 14,
                    color: textColor
                });

                // REVISION - top right
                templatePage.drawText(drawingInfo.revision, {
                    x: templateSize.width - 215,
                    y: 60,
                    size: 16,
                    color: textColor
                });

                // PROJECT - bottom right
                templatePage.drawText(drawingInfo.projectName, {
                    x: templateSize.width - 513,
                    y: 105,
                    size: 16,
                    color: textColor,
                    maxWidth: 250
                });

                // DRAWING - bottom right
                templatePage.drawText(drawingInfo.drawingTitle, {
                    x: templateSize.width - 513,
                    y: 60,
                    size: 14,
                    color: textColor,
                    maxWidth: 250
                });

                const formattedDate = drawingInfo.date.split('-').reverse().join('/');

                // DATE - bottom right in REV DATE section
                templatePage.drawText(formattedDate, {
                    x: templateSize.width - 590,
                    y: 100,
                    size: fontSize,
                    color: textColor
                });

                templatePage.drawText(drawingInfo.revision, {
                    x: templateSize.width - 635,
                    y: 100,
                    size: fontSize,
                    color: textColor
                });

                // Add logo if provided
                if (logoImageBytes) {
                    try {
                        let logoImage;
                        const logoType = drawingInfo.logo!.type;

                        if (logoType === 'image/png') {
                            logoImage = await finalPdf.embedPng(logoImageBytes);
                        } else if (logoType === 'image/jpeg' || logoType === 'image/jpg') {
                            logoImage = await finalPdf.embedJpg(logoImageBytes);
                        }

                        if (logoImage) {
                            const logoOriginalDims = logoImage.scale(1); // Get original dimensions
                            const targetHeight = 60;

                            // Calculate width maintaining aspect ratio
                            const aspectRatio = logoOriginalDims.width / logoOriginalDims.height;
                            const targetWidth = targetHeight * aspectRatio;
                            // Draw a white box
                            templatePage.drawRectangle({
                                x: 50,              // X position from left
                                y: 60,             // Y position from bottom
                                width: 250,         // Width of the box
                                height: 70,        // Height of the box
                                color: rgb(1, 1, 1), // White color (RGB values 0-1)
                            });

                            templatePage.drawImage(logoImage, {
                                x: 60,
                                y: 60,
                                width: targetWidth,
                                height: targetHeight
                            });
                        }
                    } catch (logoErr) {
                        console.error("Error embedding logo:", logoErr);
                    }
                }

                finalPdf.addPage(templatePage);
            }

            const pdfBytes = await finalPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `${drawingInfo.projectName}_${drawingInfo.drawingTitle}_drawings.pdf`;
            link.click();
            URL.revokeObjectURL(url);

        } catch (err) {
            console.error("Processing failed", err);
            alert("Something went wrong while processing the PDF. Check console for details.");
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="flex flex-col gap-6 p-6 max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold text-gray-800">PDF Drawing Template Generator</h1>

            {/* Drawing Information Form */}
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h2 className="text-lg font-semibold mb-4 text-gray-800">Drawing Information</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Project Name *
                        </label>
                        <input
                            type="text"
                            value={drawingInfo.projectName}
                            onChange={(e) => setDrawingInfo({...drawingInfo, projectName: e.target.value})}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                            placeholder="Enter project name"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Drawing Title *
                        </label>
                        <input
                            type="text"
                            value={drawingInfo.drawingTitle}
                            onChange={(e) => setDrawingInfo({...drawingInfo, drawingTitle: e.target.value})}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                            placeholder="Enter drawing title"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Revision
                        </label>
                        <input
                            type="text"
                            value={drawingInfo.revision}
                            onChange={(e) => setDrawingInfo({...drawingInfo, revision: e.target.value})}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                            placeholder="e.g., A, B, 01"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date
                        </label>
                        <input
                            type="date"
                            value={drawingInfo.date}
                            onChange={(e) => setDrawingInfo({...drawingInfo, date: e.target.value})}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Logo (PNG or JPG)
                        </label>
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={onLogoChange}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                    </div>
                </div>
            </div>

            {/* Item Code Mappings */}
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">Item Code Mappings</h2>
                    <button
                        onClick={addItemMapping}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                    >
                        + Add Item Code
                    </button>
                </div>

                <div className="space-y-3">
                    {itemMappings.map((mapping, index) => (
                        <div key={mapping.id} className="flex gap-3 items-center bg-gray-50 p-3 rounded">
                            <div className="flex-1">
                                <input
                                    type="text"
                                    value={mapping.itemCode}
                                    onChange={(e) => updateItemMapping(mapping.id, 'itemCode', e.target.value)}
                                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border text-sm"
                                    placeholder="Item Code (e.g., J01)"
                                />
                            </div>
                            <div className="w-28">
                                <input
                                    type="number"
                                    min="1"
                                    value={mapping.startPage}
                                    onChange={(e) => updateItemMapping(mapping.id, 'startPage', parseInt(e.target.value))}
                                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border text-sm"
                                    placeholder="Start"
                                />
                            </div>
                            <span className="text-gray-500">to</span>
                            <div className="w-28">
                                <input
                                    type="number"
                                    min="1"
                                    value={mapping.endPage}
                                    onChange={(e) => updateItemMapping(mapping.id, 'endPage', parseInt(e.target.value))}
                                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border text-sm"
                                    placeholder="End"
                                />
                            </div>
                            {itemMappings.length > 1 && (
                                <button
                                    onClick={() => removeItemMapping(mapping.id)}
                                    className="text-red-600 hover:text-red-800 px-2"
                                    title="Remove"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                    * Pages are numbered relative to the cropped output (starting from page 1)
                </p>
            </div>

            {/* PDF Upload and Cropping */}
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex flex-col gap-4">
                <h2 className="text-lg font-semibold text-gray-800">Source PDF</h2>
                <div className="flex gap-4 items-end">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Upload PDF</label>
                        <input
                            type="file"
                            accept="application/pdf"
                            onChange={onFileChange}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                    </div>

                    <div className="w-32">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Page #</label>
                        <input
                            type="number"
                            min="1"
                            value={startPage}
                            onChange={(e) => setStartPage(parseInt(e.target.value))}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                        />
                    </div>

                    <button
                        onClick={loadDesigner}
                        disabled={!pdfFile}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Load Page
                    </button>
                </div>
            </div>

            <div
                className={`transition-opacity duration-300 ${isReadyToDraw ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                <div className="flex justify-between items-center mb-2">
                    <h2 className="text-lg font-semibold">Draw Crop Box</h2>
                    {isReadyToDraw && (
                        <button
                            onClick={handleCropAndDownload}
                            disabled={processing}
                            className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 shadow-md font-medium"
                        >
                            {processing ? 'Processing...' : 'Generate Drawing Set'}
                        </button>
                    )}
                </div>

                <div
                    ref={designerRef}
                    className="w-full h-[600px] border-2 border-dashed border-gray-300 rounded bg-gray-50"
                />
                <p className="text-sm text-gray-500 mt-2">
                    * Resize the green box to select the area you want to keep. This area will be applied to all
                    subsequent pages and scaled to at least 70% of the template size.
                </p>
            </div>
            {processing && <LoadingScreen/>}
        </div>
    );
};

export default PdfCropper;